import React, { memo, useMemo } from "react";
import { Tree, TreeNodeInfo, Icon, Tooltip } from "@blueprintjs/core";
import { IconNames } from "@blueprintjs/icons";
import { EntityType } from "../../../../models/enums";
import { IEntityTypeDependency } from "../../../../models/EntityTypeDependency";
import { ISupportedEntityType } from "../../../../models/CustomerSyncEntityTypes";
import styles from "./SyncSchedulePanel.module.scss";

interface ISyncScheduleTreeProps {
  dependencies: IEntityTypeDependency[];
  configuredEntityTypes: EntityType[];
  originalConfiguredEntityTypes: EntityType[];
  supportedEntityTypes: ISupportedEntityType[];
  onEntityToggle: (entityType: EntityType, enabling: boolean) => void;
}

interface IEntityTreeNode extends TreeNodeInfo<EntityType> {
  nodeData: EntityType;
}

const SyncScheduleTree: React.FC<ISyncScheduleTreeProps> = memo(({
  dependencies,
  configuredEntityTypes,
  originalConfiguredEntityTypes,
  supportedEntityTypes,
  onEntityToggle
}) => {
  const nodes = useMemo(() => {
    // Create a map for quick lookup
    const depMap = new Map<EntityType, IEntityTypeDependency>();
    dependencies.forEach(dep => depMap.set(dep.entityType, dep));
    
    // Create a map for display names
    const displayNameMap = new Map<EntityType, string>();
    supportedEntityTypes.forEach(item => displayNameMap.set(item.type, item.displayName));

    // Build tree nodes
    const treeNodes: IEntityTreeNode[] = supportedEntityTypes
      .sort((a, b) => {
        const orderA = depMap.get(a.type)?.suggestedOrder ?? 999;
        const orderB = depMap.get(b.type)?.suggestedOrder ?? 999;
        return orderA - orderB;
      })
      .map(supportedEntity => {
        const entityType = supportedEntity.type;
        const displayName = supportedEntity.displayName;
        const dep = depMap.get(entityType);
        const isConfigured = configuredEntityTypes.includes(entityType);
        const wasOriginallyConfigured = originalConfiguredEntityTypes.includes(entityType);
        const isNewlyAdded = isConfigured && !wasOriginallyConfigured;
        const isBeingRemoved = wasOriginallyConfigured && !isConfigured;
        const isSupported = supportedEntityTypes.some(s => s.type === entityType);
        const hasDependencies = (dep?.dependsOn?.length ?? 0) > 0;
        const hasRequiredBy = (dep?.requiredBy?.length ?? 0) > 0;

        // Build tooltip content with bold labels
        let tooltipContent = null;
        if (hasDependencies || hasRequiredBy) {
          tooltipContent = (
            <div>
              {hasDependencies && (
                <div><strong>Requires:</strong> {dep!.dependsOn.map(et => displayNameMap.get(et) ?? et ?? "Unknown").join(', ')}</div>
              )}
              {hasRequiredBy && (
                <div><strong>Required by:</strong> {dep!.requiredBy.map(et => displayNameMap.get(et) ?? et ?? "Unknown").join(', ')}</div>
              )}
            </div>
          );
        }

        const labelContent = (
          <span>
            {displayName}
            {isNewlyAdded && <span className={`${styles.entityBadge} ${styles.entityBadgeNew}`}>(NEW)</span>}
            {isBeingRemoved && <span className={`${styles.entityBadge} ${styles.entityBadgeRemoved}`}>(REMOVED)</span>}
          </span>
        );

        return {
          id: entityType,
          nodeData: entityType,
          label: tooltipContent ? (
            <Tooltip content={tooltipContent}>
              {labelContent}
            </Tooltip>
          ) : labelContent,
          icon: isConfigured 
            ? <Icon icon={IconNames.TICK_CIRCLE} style={{ color: '#0F9960' }} className={styles.entityIconConfigured} />
            : (isSupported ? IconNames.CIRCLE : IconNames.DISABLE),
          disabled: !isSupported,
          hasCaret: false
        } as IEntityTreeNode;
      });

    return treeNodes;
  }, [dependencies, configuredEntityTypes, originalConfiguredEntityTypes, supportedEntityTypes]);

  const handleNodeClick = (node: TreeNodeInfo<EntityType>) => {
    if (node.disabled || node.nodeData === undefined) return;
    
    const entityType = node.nodeData;
    const isCurrentlyConfigured = configuredEntityTypes.includes(entityType);
    onEntityToggle(entityType, !isCurrentlyConfigured);
  };

  return (
    <Tree
      contents={nodes}
      onNodeClick={handleNodeClick}
    />
  );
});

export default SyncScheduleTree;
